import React from "react";
import { Differ, DiffResult } from "json-diff-kit";
import { removeKeysFromJson } from "../../../../helpers/jsonUtils";
import { sortCbeRecords } from "../../../../helpers/cbeUtils";
import { replaceIdInObject } from "../utils";
import {
  AccountingEventsGroup,
  CanonicalBusinessEvent,
  DigitalBusinessDocument,
  DigitalCostDocument,
} from "../../../../types";

type JsonDataType =
  | CanonicalBusinessEvent
  | AccountingEventsGroup
  | DigitalBusinessDocument
  | DigitalCostDocument;

interface ComparisonObject {
  [key: string]: unknown;
}

export const useComparison = (
  leftItem: unknown,
  rightItem: unknown,
  crossStageEnabled: boolean,
  compareStage: string,
  currentStage: string,
  overrideId: string,
  identifier: string
): [DiffResult[], DiffResult[]] | undefined => {
  const differ = React.useMemo(
    () => new Differ({ arrayDiffMethod: "lcs" }),
    []
  );

  return React.useMemo(() => {
    if (!leftItem || !rightItem) return undefined;

    let leftObject: ComparisonObject = removeKeysFromJson(
      leftItem as JsonDataType,
      ["raw_payload", "publishStatus"]
    ) as ComparisonObject;

    let rightObject: ComparisonObject = removeKeysFromJson(
      rightItem as JsonDataType,
      ["raw_payload", "publishStatus"]
    ) as ComparisonObject;

    if (
      leftObject &&
      typeof leftObject === "object" &&
      "records" in leftObject
    ) {
      leftObject = sortCbeRecords(
        leftObject as unknown as CanonicalBusinessEvent
      ) as unknown as ComparisonObject;
    }
    if (
      rightObject &&
      typeof rightObject === "object" &&
      "records" in rightObject
    ) {
      rightObject = sortCbeRecords(
        rightObject as unknown as CanonicalBusinessEvent
      ) as unknown as ComparisonObject;
    }

    if (
      crossStageEnabled &&
      compareStage !== currentStage &&
      overrideId &&
      identifier &&
      overrideId !== identifier
    ) {
      if (leftItem)
        leftObject = replaceIdInObject(
          leftObject,
          identifier,
          overrideId
        ) as ComparisonObject;
      rightObject = replaceIdInObject(
        rightObject,
        identifier,
        overrideId
      ) as ComparisonObject;
    }

    const diffResult = differ.diff(leftObject, rightObject);
    return [...diffResult] as [DiffResult[], DiffResult[]];
  }, [
    leftItem,
    rightItem,
    crossStageEnabled,
    compareStage,
    currentStage,
    overrideId,
    identifier,
    differ,
  ]);
};
